import {Trans} from '@lingui/macro';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Toggle} from '../../../../../../../libs/shared/ui/src/components/booleanInputs/toggle';
import {Button} from '../../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useLinkNavigation} from '../../../../../../components/common/navigation/useLinkNavigation';
import {useAppDispatch, useAppSelector} from '../../../../../../helpers/reduxHelpers';
import {
  IntegrationSettingsTypesEnum,
  JiraIntegrationSettingsModel,
} from '../../../../../../models/api/integrationModel';
import {
  signInToIntegrationAccountAction,
  signInToJiraSelfHostedIntegrationAccountAction,
  signOutOfIntegrationAsyncAction,
} from '../../../../../../store/data/integrations/integrationsActions';
import {getIsUserGlobalAdmin} from '../../../../../../store/data/teammates/teammatesSelectors';
import {SettingsAppsConfigureJiraSelfHosted} from './settingsAppsConfigureJiraSelfHosted';

/*
 * Constants.
 */

const seeHowItWorksLink = 'http://help.frontapp.com/t/y76nhl/how-to-enable-and-use-the-jira-integration';

/*
 * Props.
 */

interface SettingsAppsConfigureJiraProps {
  configurationSettings?: JiraIntegrationSettingsModel;
  onIsSaveEnabled: HandlerOf<boolean>;
  setConfigurationSettings: HandlerOf<JiraIntegrationSettingsModel | undefined>;
}

/*
 * Style.
 */

const StyledSeeHowItWorksWrapperDiv = styled.div`
  display: flex;
  margin-left: -14px;
  margin-bottom: 16px;
`;

const StyledButtonWrapperDiv = styled.div`
  display: flex;
  margin-top: 8px;
`;

const StyledToggleWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  margin-bottom: 16px;
  align-items: center;
`;

const StyledToggleDescriptionWrapperDiv = styled.div`
  flex: 1;
  font-weight: ${fontWeights.bold};
`;

/*
 * Component.
 */

export const SettingsAppsConfigureJira: FC<SettingsAppsConfigureJiraProps> = (props) => {
  const {configurationSettings, setConfigurationSettings, onIsSaveEnabled} = props;
  const {navigateToLink} = useLinkNavigation();
  const dispatch = useAppDispatch();
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);

  const [isSelfHosted, setIsSelfHosted] = useState(Boolean(configurationSettings?.jiraServer));

  // When keys are generated/revoked it pushes a new update to the integration model, this will watch the local
  // value and make sure it is not cleared when that happens.
  useEffect(() => {
    if (configurationSettings?.jiraServer !== isSelfHosted) {
      setConfigurationSettings({
        ...configurationSettings,
        jiraServer: isSelfHosted,
      });
    }
  }, [isSelfHosted, setConfigurationSettings, configurationSettings]);

  // Controls if save is enabled for this dialog.
  //  This requires the api-key to be filled out, but there is no validation.
  useEffect(() => {
    if (isSelfHosted && !configurationSettings?.jiraServerUrl) {
      onIsSaveEnabled(false);
      return;
    }
    onIsSaveEnabled(true);
  }, [configurationSettings, isSelfHosted, onIsSaveEnabled]);

  const onToggleChange = (val: boolean) => {
    if (!isGlobalAdmin) {
      return;
    }
    setIsSelfHosted(val);
    setConfigurationSettings({
      ...configurationSettings,
      jiraServer: val,
    });
  };

  const onSignInClick = () => {
    if (isSelfHosted) {
      dispatch(signInToJiraSelfHostedIntegrationAccountAction(IntegrationSettingsTypesEnum.JIRA, 'JIRA'));
      return;
    }
    dispatch(signInToIntegrationAccountAction(IntegrationSettingsTypesEnum.JIRA, 'JIRA'));
  };

  const onSignOutClick = () => {
    dispatch(signOutOfIntegrationAsyncAction(IntegrationSettingsTypesEnum.JIRA));
  };

  return (
    <>
      <StyledToggleWrapperDiv>
        <StyledToggleDescriptionWrapperDiv onClick={() => onToggleChange(!isSelfHosted)}>
          <Trans id="settings-apps-configure-app-dialog-jira-self-hosted-toggle">
            Connect to your self-hosted JIRA server
          </Trans>
        </StyledToggleDescriptionWrapperDiv>
        <Toggle
          size={VisualSizesEnum.SMALL}
          isChecked={isSelfHosted}
          onChange={onToggleChange}
          isDisabled={!isGlobalAdmin}
        />
      </StyledToggleWrapperDiv>
      <Trans id="settings-apps-configure-app-dialog-jira-description">
        Adds an &quot;Add to JIRA&quot; item to the conversation menu.
      </Trans>
      <StyledSeeHowItWorksWrapperDiv>
        <Button variant={ButtonVariantsEnum.LINK} onClick={() => navigateToLink(seeHowItWorksLink)}>
          <Trans id="settings-apps-configure-app-dialog-jira-see-how-it-works">See how it works</Trans>
        </Button>
      </StyledSeeHowItWorksWrapperDiv>
      <SettingsAppsConfigureJiraSelfHosted
        configurationSettings={configurationSettings}
        setConfigurationSettings={setConfigurationSettings}
      />
      {maybeRenderAuthContent(
        configurationSettings,
        isSelfHosted,
        isGlobalAdmin,
        onSignInClick,
        onSignOutClick,
      )}
    </>
  );
};

/*
 * Helpers.
 */

function maybeRenderAuthContent(
  configurationSettings: JiraIntegrationSettingsModel | undefined,
  isSelfHosted: boolean,
  isGlobalAdmin: boolean,
  onSignInClick: Handler,
  onSignOutClick: Handler,
) {
  // Sign out content.
  if (configurationSettings?.readonly?.login) {
    return (
      <>
        <Trans id="settings-apps-configure-app-dialog-jira-connected-description">
          Your team is connected to JIRA as <strong>${configurationSettings.readonly.login}</strong>. Now when
          an issue is resolved, the linked conversations in Front will be reopened automatically.
        </Trans>
        <StyledButtonWrapperDiv>
          <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onSignOutClick} isDisabled={!isGlobalAdmin}>
            <Trans id="settings-apps-configure-app-dialog-jira-sign-out-button">Sign out of JIRA</Trans>
          </Button>
        </StyledButtonWrapperDiv>
      </>
    );
  }
  // Sign in content.
  return (
    <>
      <Trans id="settings-apps-configure-app-dialog-jira-not-connected-description">
        If you sign in here, when an issue is resolved in JIRA the linked conversations in Front will be
        reopened automatically.
      </Trans>
      <StyledButtonWrapperDiv>
        <Button
          variant={ButtonVariantsEnum.PRIMARY}
          onClick={onSignInClick}
          isDisabled={Boolean(isSelfHosted && !configurationSettings?.jiraServerUrl) || !isGlobalAdmin}
        >
          {!isSelfHosted && (
            <Trans id="settings-apps-configure-app-dialog-jira-sign-in-button">Sign in to JIRA</Trans>
          )}
          {isSelfHosted && (
            <Trans id="settings-apps-configure-app-dialog-jira-sign-in-self-hosted-button">
              Sign in using OAuth
            </Trans>
          )}
        </Button>
      </StyledButtonWrapperDiv>
    </>
  );
}
